import { Suspense, lazy, useEffect, useMemo, useRef, useState } from "react";
import { BarChart3, Expand } from "lucide-react";
import { Button } from "~/components/ui/button";
import type { ArtifactPreview } from "~/services/artifacts";
import { ArtifactShell, useArtifactExpand } from "./ArtifactShell";

// Lazy-loaded — react-plotly.js pulls in the full ~3MB plotly.js bundle.
const Plot = lazy(() => import("react-plotly.js"));

const INLINE_HEIGHT = 400;
const EXPANDED_HEIGHT = 720;
const COMPACT_WIDTH_PX = 350;

interface PlotlyFigureJson {
  data?: unknown[];
  layout?: Record<string, unknown> & {
    margin?: { l?: number; r?: number; t?: number; b?: number };
  };
  frames?: unknown[];
  config?: Record<string, unknown>;
}

function asPlotlyFigure(content: unknown): PlotlyFigureJson | null {
  if (!content || typeof content !== "object" || Array.isArray(content)) return null;
  const c = content as PlotlyFigureJson;
  if (!Array.isArray(c.data)) return null;
  return c;
}

const Fallback = () => (
  <div className="flex h-64 items-center justify-center text-sm text-muted-foreground">
    Loading chart…
  </div>
);

const FigureBody = ({
  figure,
  height,
  artifactId,
  fullscreen,
}: {
  figure: PlotlyFigureJson;
  height: number;
  artifactId: string;
  fullscreen: boolean;
}) => {
  // Clear any width/height the upstream figure JSON declared so Plotly
  // sizes to the container rather than its own fixed pixel size.
  const layout = useMemo<Partial<Plotly.Layout>>(() => {
    const figureLayout = figure.layout ?? {};
    const figureMargin = figureLayout.margin ?? {};
    return {
      ...figureLayout,
      width: undefined,
      height: undefined,
      autosize: true,
      margin: {
        l: figureMargin.l ?? 50,
        r: figureMargin.r ?? 50,
        t: figureMargin.t ?? 50,
        b: figureMargin.b ?? 50,
      },
    } as Partial<Plotly.Layout>;
  }, [figure]);
  const config = useMemo<Partial<Plotly.Config>>(
    () =>
      ({
        displaylogo: false,
        responsive: true,
        displayModeBar: "hover",
        modeBarButtons: [
          ["zoomIn2d", "zoomOut2d", "pan2d", "resetScale2d"],
          ["hoverClosestCartesian", "hoverCompareCartesian"],
          ["toImage"],
        ],
        ...(figure.config ?? {}),
      }) as Partial<Plotly.Config>,
    [figure]
  );
  const data = useMemo(() => (figure.data ?? []) as Plotly.Data[], [figure]);
  // Plotly keys internal state by element id; distinct ids keep inline and
  // modal instances from corrupting each other.
  const divId = fullscreen ? `plot-fs-${artifactId}` : `plot-${artifactId}`;
  return (
    <Suspense fallback={<Fallback />}>
      <Plot
        data={data}
        layout={layout}
        config={config}
        useResizeHandler
        style={{ width: "100%", height }}
        divId={divId}
      />
    </Suspense>
  );
};

const CompactPlaceholder = ({ onExpand }: { onExpand: () => void }) => (
  <div className="flex min-h-48 items-center justify-center rounded-md px-4 py-5">
    <div className="flex max-w-xs flex-col items-center gap-3 text-center">
      <p className="text-xs text-muted-foreground">This chart is easier to read in fullscreen.</p>
      <Button type="button" variant="outline" size="sm" onClick={onExpand} className="gap-1.5">
        <Expand className="size-3.5" />
        View this chart
      </Button>
    </div>
  </div>
);

const InlineFigure = ({ figure, artifactId }: { figure: PlotlyFigureJson; artifactId: string }) => {
  const requestExpand = useArtifactExpand();
  const ref = useRef<HTMLDivElement>(null);
  const [isCompact, setIsCompact] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => {
      if (!entry) return;
      setIsCompact(entry.contentRect.width <= COMPACT_WIDTH_PX);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref}>
      {isCompact && requestExpand ? (
        <CompactPlaceholder onExpand={requestExpand} />
      ) : (
        <FigureBody
          figure={figure}
          height={INLINE_HEIGHT}
          artifactId={artifactId}
          fullscreen={false}
        />
      )}
    </div>
  );
};

export const FigureArtifact = ({ preview }: { preview: ArtifactPreview }) => {
  const figure = asPlotlyFigure(preview.content);

  if (!figure) {
    return (
      <ArtifactShell preview={preview}>
        <div className="flex items-center gap-3 py-4 text-sm text-muted-foreground">
          <BarChart3 className="size-5" aria-hidden />
          <span>Figure preview is not a Plotly payload.</span>
        </div>
      </ArtifactShell>
    );
  }

  return (
    <ArtifactShell
      preview={preview}
      expandedContent={
        <FigureBody
          figure={figure}
          height={EXPANDED_HEIGHT}
          artifactId={preview.artifact_id}
          fullscreen
        />
      }
    >
      <InlineFigure figure={figure} artifactId={preview.artifact_id} />
    </ArtifactShell>
  );
};
